import type { ComponentType } from "react";
import { useEffect, useRef } from "react";

import { Navigate, useLocation } from "react-router-dom";

import Routers from "../constants/routers";
import { useAuth } from "../context/AuthContext";
import { getToken } from "../utils/common/common";

export const notAuth = <P extends object>(WrappedComponent: ComponentType<P>) => {
  const isAuthenticated = getToken();

  const AuthenticatedComponent: React.FC<P> = (props) => {
    if (!isAuthenticated) {
      return <WrappedComponent {...props} />;
    }
    return <Navigate to={Routers.Dashboard} />;
  };

  return AuthenticatedComponent;
};

export const withAuth = <P extends object>(WrappedComponent: ComponentType<P>) => {
  const AuthenticatedComponent: React.FC<P> = (props) => {
    const { user, fetchUser } = useAuth();
    const hasFetchedRef = useRef(false);
    const token = getToken();
    const isAuthenticated = !!token;
    const location = useLocation();

    // Fetch user when entering protected page if user is not available and token exists
    useEffect(() => {
      if (isAuthenticated && !user && !hasFetchedRef.current) {
        hasFetchedRef.current = true;
        fetchUser();
      }
    }, [isAuthenticated, user, fetchUser]);

    if (isAuthenticated) {
      return <WrappedComponent {...props} />;
    }
    return <Navigate to={Routers.Login} state={{ from: location }} />;
  };

  return AuthenticatedComponent;
};
